import { useState, useRef } from "react";
import { UploadSimple, DownloadSimple, CheckCircle, Warning, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";

function ImportCard({ kind, title, desc, columns }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a CSV or Excel file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} ${kind} imported`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid={`import-card-${kind}`}>
      <div className="flex items-center gap-2 mb-1"><FileArrowUp size={22} className="text-primary" /><span className="font-head font-semibold text-lg">{title}</span></div>
      <p className="text-sm text-muted-foreground mb-4">{desc}</p>

      <div className="text-xs text-muted-foreground mb-4">
        <span className="overline">Expected columns</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {columns.map((c) => <span key={c} className="px-2 py-0.5 rounded-sm border border-border bg-secondary/50">{c}</span>)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <a href={`${API}/import/template/${kind}`} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm" data-testid={`import-template-${kind}`}><DownloadSimple size={15} className="mr-1.5" /> Download template</Button>
        </a>
        <input data-testid={`import-file-${kind}`} type="file" ref={fileRef} accept=".csv,.xlsx,.xls" className="text-sm" />
        <Button size="sm" data-testid={`import-run-${kind}`} onClick={run} disabled={busy}>
          <UploadSimple size={15} className="mr-1.5" /> {busy ? "Importing…" : "Import"}
        </Button>
      </div>

      {result && (
        <div className="mt-4 border-t border-border pt-4">
          <div className="text-sm flex items-center gap-1.5 text-emerald-700"><CheckCircle size={16} weight="fill" /> {result.created} of {result.total} rows imported</div>
          {result.errors?.length > 0 && (
            <div className="mt-2">
              <div className="text-sm text-alert flex items-center gap-1.5"><Warning size={15} /> {result.errors.length} row error(s):</div>
              <ul className="text-xs text-muted-foreground list-disc pl-5 mt-1 space-y-0.5">
                {result.errors.slice(0, 8).map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Import() {
  return (
    <div>
      <PageHeader overline="Import / Integration-Ready" title="Bulk Import">
        Seed records from a CSV or Excel template — the manual-entry fallback for the integration-ready architecture.
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-6">
        <ImportCard
          kind="contracts"
          title="Import Contracts"
          desc="Create multiple contract records at once. Each row becomes a draft contract in intake."
          columns={["title", "contract_number", "contract_type", "procurement_method", "grantee_vendor", "program", "amount", "start_date", "end_date"]}
        />
        <ImportCard
          kind="grants"
          title="Import Grants"
          desc="Create multiple grant funding records at once."
          columns={["title", "funding_source", "grant_amount", "grant_period_start", "grant_period_end", "reporting_obligations", "compliance_status"]}
        />
      </div>
    </div>
  );
}
